import * as React from 'react';
import { Dispatch } from 'react-redux';

import { StoreState } from '../../../../reducers';
import { Wrapper, ImageWrapper, Image } from './styles';
import EditPanel from '../../../../containers/Editor/EditPanel';
import SpreadBackgroundPanel from '../../../../containers/Editor/EditPanel/SpreadBackgroundPanel';
import SliderPanel from '../../../../containers/Editor/EditPanel/SpreadBackgroundPanel/SliderPanel';
import { EditableType, SPREAD_BACKGROUND_CLICK_CLASSNAME } from '../../../../containers/Editor/constants';
import { setIsSpreadBackgroundActive } from '../../../../containers/Editor/actions';
import { BackgroundMediaType, Position, PositionConfig } from '../../../../containers/Editor/reducer';
import Utils from '../../../../utils';

interface BackgroundProps {
	url: string;
	colors: (string | undefined)[] | undefined;
	position: PositionConfig | undefined;
	isHidden: boolean;
	isEditable: boolean;
	isActive: boolean;
	isEditPanelDisplayed: boolean;
	mediaType: BackgroundMediaType;
	defaultMediaType: BackgroundMediaType;
	editorContainer: Element | null;
	children: React.ReactNode;
	thumbnailsWidth: number;
	dispatch: Dispatch<StoreState>;
}

export interface FilledPositionConfig {
	desktop: Position;
	phone: Position;
}

export enum PreviewMode {
	DESKTOP = 'desktop',
	PHONE = 'phone',
}

const getPanels = (
	defaultMediaType: BackgroundMediaType,
	position: FilledPositionConfig,
	onPositionChange: (value: FilledPositionConfig) => void,
) => {
	const props = {
		position,
		onPositionChange,
	};

	const panels = {
		[EditableType.image]: (
			<SpreadBackgroundPanel
				type={EditableType.image}
				{...props}
			/>
		),
		[EditableType.video]: (
			<SpreadBackgroundPanel
				type={EditableType.video}
				{...props}
			/>
		),
	};

	if (defaultMediaType === BackgroundMediaType.SLIDER) {
		panels[EditableType.slider] = <SliderPanel />;
	}

	return panels;
};

const isClickOnBackground = (e: MouseEvent | TouchEvent): boolean => {
	const element = e.target as HTMLElement;
	return element.classList.contains(SPREAD_BACKGROUND_CLICK_CLASSNAME);
};

const getDefaultPanelType = (mediaType: BackgroundMediaType): EditableType => {
	switch (mediaType) {
		case BackgroundMediaType.IMAGE:
			return EditableType.image;
		case BackgroundMediaType.MOVIE:
			return EditableType.video;
		case BackgroundMediaType.SLIDER:
			return EditableType.slider;
	}
};

export const DEFAULT_SCALE = 1;
const DEFAULT_TOP = 50; // %
const DEFAULT_LEFT = 50; // %
const DEFAULT_POSITION = {
	scale: DEFAULT_SCALE,
	top: DEFAULT_TOP,
	left: DEFAULT_LEFT,
};
const previewModes = [PreviewMode.DESKTOP, PreviewMode.PHONE];

const getInitialValue = (value: number | undefined, defaultValue: number): number => (
	value === undefined ? defaultValue : value
);

const getInitialPosition = (position: PositionConfig | undefined): FilledPositionConfig => {
	let initialPosition = position ? Utils.deepCopy(position) : undefined;

	if (!initialPosition) {
		return {
			desktop: DEFAULT_POSITION,
			phone: DEFAULT_POSITION,
		};
	}

	for (let i = 0; i < previewModes.length; i++) {
		const mode = previewModes[i];

		if (!initialPosition[mode]) {
			initialPosition[mode] = DEFAULT_POSITION;
		} else {
			initialPosition[mode] = {
				scale: getInitialValue(initialPosition[mode]!.scale, DEFAULT_SCALE),
				top: getInitialValue(initialPosition[mode]!.top, DEFAULT_TOP),
				left: getInitialValue(initialPosition[mode]!.left, DEFAULT_LEFT),
			};
		}
	}

	return initialPosition as FilledPositionConfig;
};

const Background = ({
	url,
	colors,
	position: initialPosition,
	isHidden,
	isEditable,
	isActive,
	isEditPanelDisplayed,
	mediaType,
	defaultMediaType,
	editorContainer,
	children,
	thumbnailsWidth,
	dispatch,
}: BackgroundProps) => {
	const [position, setPosition] = React.useState(() => getInitialPosition(initialPosition));

	const handleClick = (e: MouseEvent | TouchEvent) => {
		if ((!url && defaultMediaType !== BackgroundMediaType.SLIDER) || !isEditable || isActive) {
			return;
		}

		if (isClickOnBackground(e)) {
			dispatch(setIsSpreadBackgroundActive(true));
		}
	};

	React.useEffect(() => {
		setPosition(getInitialPosition(initialPosition));
	}, [initialPosition]);

	React.useEffect(() => {
		if (!isActive) {
			return;
		}

		const handleWindowClick = (e: MouseEvent | TouchEvent) => {
			if (!isClickOnBackground(e)) {
				dispatch(setIsSpreadBackgroundActive(false));
			}
		};

		if (editorContainer) {
			editorContainer.addEventListener('mousedown', handleWindowClick);
		}

		return () => {
			if (editorContainer) {
				editorContainer.removeEventListener('mousedown', handleWindowClick);
			}
		};
	}, [isActive]);

	return (
		<Wrapper
			isHidden={isHidden}
			colors={colors}
			onClick={handleClick}
		>
			{isActive && isEditPanelDisplayed &&
				<EditPanel
					defaultType={getDefaultPanelType(mediaType)}
					panels={getPanels(defaultMediaType, position, setPosition)}
				/>
			}
			{(url || defaultMediaType === BackgroundMediaType.SLIDER) &&
				<ImageWrapper
					className={SPREAD_BACKGROUND_CLICK_CLASSNAME}
					isActive={isActive}
				>
					<Image
						url={url}
						position={position}
						thumbnailsWidth={thumbnailsWidth}
						className={SPREAD_BACKGROUND_CLICK_CLASSNAME}
					/>
				</ImageWrapper>
			}
			{children}
		</Wrapper>
	);
};

export default Background;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/Spread/Background/index.tsx